import Image from 'next/legacy/image'
import Script from 'next/script'
import DefaultContentLayout from '@/layouts/defaultContent'

// Components

import Head from '@/components/Head'
import Button from '@/components/ui/button'
import DynamicFaq from '@/components/DynamicFaq'
import Trans from '@/components/i18n/Trans'

// Utils
import api from '@/services/api'

// Hooks

import useTranslation from '@/hooks/translation'
import useBreakpoints from '@/hooks/breakpoints'
import useCurrency from '@/hooks/currency'

// Styles
import styles from './FlightClub.module.css'

// Assets

import Header from '@/assets/img/flight-club/header.jpg'
import HeaderMobile from '@/assets/img/flight-club/headerM.jpg'
import Prio from '@/assets/img/flight-club/prio.jpg'
import Garanty from '@/assets/img/flight-club/garanty.jpg'
import Price from '@/assets/img/flight-club/price.jpg'
import FlightClubCard from '@/assets/img/flight-club/card.png'
import Check from '@/assets/img/flight-club/check.svg'
import StripeClimate from '@/assets/img/flight-club/stripeClimate.svg'

import Vogue from '@/assets/img/flight-club/logos/vogue.png'
import Echos from '@/assets/img/flight-club/logos/echos.png'
import BFM from '@/assets/img/flight-club/logos/bfm.png'
import Reclame from '@/assets/img/flight-club/logos/reclame.png'
import France2 from '@/assets/img/flight-club/logos/france2.png'
import Grazia from '@/assets/img/flight-club/logos/grazia.png'

import useUser from '@/hooks/user'
import { useRouter } from 'next/router'

function FlightClub() {
  const { t } = useTranslation()
  const { isTablet } = useBreakpoints()
  const { user } = useUser()
  const router = useRouter()
  const { formatCurrency } = useCurrency()

  const faq = {
    items: [
      {
        id: 1,
        question: t('pages.flightClub.faq.items.1.question'),
        answer: t('pages.flightClub.faq.items.1.answer'),
      },
      {
        id: 2,
        question: t('pages.flightClub.faq.items.2.question'),
        answer: t('pages.flightClub.faq.items.2.answer'),
      },
      {
        id: 3,
        question: t('pages.flightClub.faq.items.3.question'),
        answer: t('pages.flightClub.faq.items.3.answer'),
      },
      {
        id: 4,
        question: t('pages.flightClub.faq.items.4.question'),
        answer: t('pages.flightClub.faq.items.4.answer'),
      },
    ],
  }

  const gradient = {
    background: 'linear-gradient(180deg, #161c27 0%, #141622 100%)',
  }

  const subscribeToFlightClub = () => {
    if (!user?.id) {
      router.push('/flight-club/register')
    } else {
      api.checkout.createFlightClubSubscription().then((resp) => {
        window.location.href = resp.redirect_url
      })
    }
  }

  return (
    <div className="bg-[#F3F4F9]">
      <Head
        title={t('pages.flightClub.seo.title')}
        description={t('pages.flightClub.seo.description')}
      />
      <Script
        strategy="afterInteractive"
        src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
      />

      {/* HERO */}
      <header
        style={gradient}
        className="w-full md:h-[1300px] h-[970px]  relative overflow-hidden"
      >
        <Image
          src={isTablet ? HeaderMobile : Header}
          layout="fill"
          objectFit="contain"
          quality={100}
          priority
          placeholder="blur"
          alt=""
          objectPosition="bottom"
        />
        {/* TITLES */}
        <section className="container absolute inset-0 mt-100 md:mt-70  ">
          <div className="text-center">
            <h1 className="text-60 md:text-[80px] lg:text-[80px] font-bold text-shadow text-white leading-none mt-30">
              {t('pages.flightClub.title')}
            </h1>

            <p className="text-20 md:text-26 text-shadow text-white m-30 md:m-10 mt-0 leading-[24px] mt-0 font-bold">
              <Trans
                i18nKey="pages.flightClub.description"
                components={{
                  span: <span className="block font-bold" />,
                }}
              />
            </p>

            <ul className="text-20 text-white md:mt-30 mt-50">
              <li className="md:inline-block md:mx-20 my-20">
                <Check className="mr-10 inline-block mt-[-3px]" />
                {t('pages.flightClub.list.checkin')}
              </li>
              <li className="md:inline-block md:mx-20 my-20">
                <Check className="mr-10 inline-block mt-[-3px]" />
                {t('pages.flightClub.list.priority')}
              </li>
              <li className="md:inline-block md:mx-20 my-20">
                <Check className="mr-10 inline-block mt-[-3px]" />
                {t('pages.flightClub.list.exclusive')}
              </li>
            </ul>

            <div className="flex items-center justify-center my-20">
              <div className="font-bold text-50 text-white leading-none mr-10">
                {formatCurrency(99, 'EUR')}
              </div>
              <div className="flex flex-col">
                <div
                  className={`mb-3 text-[25px] text-[#B9BBC1] relative leading-none ${styles.strokedPrice}`}
                >
                  {formatCurrency(149, 'EUR')}
                </div>
                <div className="text-16 text-white leading-none text-left">
                  /{t('pages.flightClub.perYear')}
                </div>
              </div>
              <div className="text-white text-30 mt-[-10px] ml-3 self-start">
                *
              </div>
            </div>

            <Button
              className={`mx-auto mt-30 px-50 py-18 text-20 font-bold bg-primary rounded-[100px] text-white motion-safe:transition-all motion-safe:duration-300 drop-shadow-[0px 14px 24px rgba(0, 0, 0, 0.5] ${styles.primaryGradient} hover:-translate-y-10`}
              unstyled
              onClick={subscribeToFlightClub}
            >
              {t('pages.flightClub.cta')}
              <svg
                width="20"
                height="16"
                viewBox="0 0 20 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="inline-block ml-30"
              >
                <path
                  d="M19.7071 8.70711C20.0976 8.31658 20.0976 7.68342 19.7071 7.29289L13.3431 0.928932C12.9526 0.538408 12.3195 0.538408 11.9289 0.928932C11.5384 1.31946 11.5384 1.95262 11.9289 2.34315L17.5858 8L11.9289 13.6569C11.5384 14.0474 11.5384 14.6805 11.9289 15.0711C12.3195 15.4616 12.9526 15.4616 13.3431 15.0711L19.7071 8.70711ZM0 9H19V7H0V9Z"
                  fill="white"
                />
              </svg>
            </Button>

            <p className="text-white mt-30 text-18 text-center">
              * {t('pages.flightclub.introductoryOffer')}
            </p>
          </div>
        </section>
      </header>

      {/* CARDS */}
      <section className="container md:-mt-150  md:mb-130 relative md:px-0 px-30 pb-50 md:pb-0">
        <p
          id="discover"
          className="text-40 font-bold leading-[44px] md:text-left  text-center text-dark md:text-white mb-30 tracking-[-1px] mt-30 md:mt-0 "
        >
          <Trans
            i18nKey="pages.flightClub.club.title"
            components={{
              span: <span className="block" />,
            }}
          />
        </p>
        <div className="md:-mb-70 ">
          <div className="row ">
            <Card
              image={Prio}
              title={
                <Trans
                  i18nKey="pages.flightClub.club.cards.priority.title"
                  components={{
                    span: <span className="block text-primary" />,
                  }}
                />
              }
              description={
                <Trans
                  i18nKey="pages.flightClub.club.cards.priority.description"
                  components={{
                    span: <span className="font-bold" />,
                  }}
                />
              }
            />
            <Card
              image={Garanty}
              title={
                <Trans
                  i18nKey="pages.flightClub.club.cards.garanty.title"
                  components={{
                    span: <span className="block text-primary" />,
                  }}
                />
              }
              description={
                <Trans
                  i18nKey="pages.flightClub.club.cards.garanty.description"
                  components={{
                    span: <span className="font-bold" />,
                  }}
                />
              }
            />
            <Card
              image={Price}
              title={
                <Trans
                  i18nKey="pages.flightClub.club.cards.price.title"
                  components={{
                    span: <span className="block text-primary" />,
                  }}
                />
              }
              description={
                <Trans
                  i18nKey="pages.flightClub.club.cards.price.description"
                  components={{
                    span: <span className="font-bold" />,
                  }}
                />
              }
            />
          </div>
        </div>
      </section>
      {/* SECOURS POPULAIRE */}
      <section className="relative grid grid-cols-12 gap-30">
        <div className="container md:px-0 text-center pb-140 col-start-3 col-end-11">
          <p className="text-40 font-bold leading-10 my-30 mb-40">
            <Trans
              i18nKey="pages.flightClub.stripeClimate.title"
              components={{
                span: <span className="block text-primary mb-30" />,
              }}
            />
          </p>
          <p className="text-dark-60 text-17 max-w-4xl m-auto leading-7">
            {t('pages.flightClub.stripeClimate.content')}
          </p>
          <div className="text-center md:mb-120 mb-0 mt-40">
            <StripeClimate className="inline-block" />
          </div>
        </div>
      </section>
      {/* PRICING */}
      <section className="bg-[#f3f4f9] border-b  border-dark-10">
        <div className="container md:px-0 grid gap-30 relative md:-top-110 -top-90  grid-cols-1 md:grid-cols-2 px-30 ">
          <article className="rounded-5 bg-white md:p-60 p-30 border-dark-10 border ">
            <p className="text-white font-bold text-dark text-[36px]  leading-[36px] md:text-50 md:leading-[53px] text-center md:text-left ">
              <Trans
                i18nKey="pages.flightClub.plan.title"
                components={{
                  span: <span className="block text-primary" />,
                }}
              />
            </p>

            <div className="flex items-center mt-20">
              <div className="font-bold text-50 text-dark leading-none mr-10">
                {formatCurrency(99, 'EUR')}
              </div>
              <div className="flex flex-col">
                <div
                  className={`mb-3 text-[25px] text-[#57637A] relative leading-none ${styles.strokedPrice}`}
                >
                  {formatCurrency(149, 'EUR')}
                </div>
                <div className="text-16 text-dark leading-none text-left">
                  /{t('pages.flightClub.perYear')}
                </div>
              </div>
              <div className="text-dark text-30 mt-[-10px] ml-3 self-start">
                *
              </div>
            </div>

            <p className="text-dark opacity-40 mt-10 mb-40">
              * {t('pages.flightclub.introductoryOffer')}
            </p>

            <ul className="text-dark text-18">
              <li className=" flex mb-20">
                <div className="text-primary mr-10">
                  <Check />
                </div>
                {t('pages.flightClub.list.priority')}
              </li>
              <li className=" flex mb-20">
                <div className="text-primary mr-10">
                  <Check />
                </div>
                {t('pages.flightClub.plan.list.checkin')}
              </li>
              <li className=" flex mb-20">
                <div className="text-primary mr-10">
                  <Check />
                </div>
                {t('pages.flightClub.plan.list.price')}
              </li>
              <li className=" flex mb-20">
                <div className="text-primary mr-10">
                  <Check />
                </div>
                {t('pages.flightClub.plan.list.child')}
              </li>
            </ul>
            <div className="mt-40">
              <Button
                className={`mx-auto mt-30 px-50 py-18 text-20 font-bold bg-primary rounded-[100px] text-white motion-safe:transition-all motion-safe:duration-300 drop-shadow-[0px 14px 24px rgba(0, 0, 0, 0.5] ${styles.primaryGradient} hover:-translate-y-10`}
                unstyled
                onClick={subscribeToFlightClub}
              >
                {t('pages.flightClub.cta')}

                <svg
                  width="20"
                  height="16"
                  viewBox="0 0 20 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="inline-block ml-30"
                >
                  <path
                    d="M19.7071 8.70711C20.0976 8.31658 20.0976 7.68342 19.7071 7.29289L13.3431 0.928932C12.9526 0.538408 12.3195 0.538408 11.9289 0.928932C11.5384 1.31946 11.5384 1.95262 11.9289 2.34315L17.5858 8L11.9289 13.6569C11.5384 14.0474 11.5384 14.6805 11.9289 15.0711C12.3195 15.4616 12.9526 15.4616 13.3431 15.0711L19.7071 8.70711ZM0 9H19V7H0V9Z"
                    fill="white"
                  />
                </svg>
              </Button>
            </div>
          </article>
          <article className="rounded-5 relative overflow-hidden h-[550px] md:h-auto">
            <Image
              src={FlightClubCard}
              objectFit="cover"
              layout="fill"
              quality={100}
              priority
              placeholder="blur"
              alt=""
              height="120%"
              width="120%"
            />
          </article>
        </div>
      </section>

      <section className="bg-[#EBEDF5] py-20">
        <div className="flex justify-around container items-center">
          <div>
            <Image
              alt="logo"
              src={Vogue}
              height={38}
              objectFit="contain"
              quality={100}
              width={290}
            />
          </div>
          <div>
            <Image
              alt="logo"
              src={Echos}
              height={38}
              objectFit="contain"
              quality={100}
              width={329}
            />
          </div>
          <div>
            <Image
              alt="logo"
              src={BFM}
              height={51}
              objectFit="contain"
              quality={100}
              width={164}
            />
          </div>
          <div>
            <Image
              alt="logo"
              src={Reclame}
              height={69}
              objectFit="contain"
              quality={100}
              width={214}
            />
          </div>
          <div>
            <Image
              alt="logo"
              src={France2}
              height={59}
              objectFit="contain"
              quality={100}
              width={188}
            />
          </div>
          <div>
            <Image
              alt="logo"
              src={Grazia}
              height={38}
              objectFit="contain"
              quality={100}
              width={238}
            />
          </div>
        </div>
      </section>

      <section className="bg-[#F9F9FC] relative">
        <div className="container md:pt-90 pt-70 pb-110">
          <p className="text-30 font-bold text-dark mb-20 text-center md:text-left">
            {t('pages.flightClub.faq.title')}
          </p>
          <div className="bg-white">
            <DynamicFaq faq={faq} />
          </div>
        </div>
      </section>
    </div>
  )
}

export function Card({ image, title, description }) {
  return (
    <article className="md:col-6 mb-30 lg:col-4 hover:top-10 relative motion-safe:transition-all motion-safe:duration-150 top-0 hover:drop-shadow-2xl">
      <header className="bg-primary-5 relative flex rounded-10 overflow-hidden">
        <Image
          src={image}
          objectFit="fill"
          quality={100}
          placeholder="blur"
          alt=""
          className="hover:shadow-double"
        />
      </header>
      <p className="md:mt-20 mt-30 text-30 leading-[31px] font-bold text-center md:text-left">
        {title}
      </p>
      <p className="mt-20 text-17 text-dark-60 text-center md:text-left ">
        {description}
      </p>
    </article>
  )
}

FlightClub.layout = DefaultContentLayout
export default FlightClub
